import React, { useEffect, useState } from 'react';
import {
  FilterToggleButton,
  ClientDrawer,
  CopySavedViewLinkButton,
  SavedViewButtonsTrackingTags,
} from '@ecosystem/newron-client-lib';
import { Context, EventName, PageHeading, Params, UiAction } from '../../types';
import { ContextManager } from '../../context/manager';
import { PageTitle } from './title';
import { useUiAction } from '../../ui.actions';
import { SavedViewVersion, UseRestoreSavedViewV1 } from '../../tools/useSavedView';
import { PageSettingsManager } from '../settings';
import './header.css';
import { useAnalyticsContext } from '../../analytics/analytics.provider';
import { getFilterEventDetails } from '../../filters/utils';
import { getAppliedFilters } from '../../analytics/trackPageElementViewed.hook';
import { shouldDisableFilterTray, shouldDisableCopyLink } from './utils';
import { PageInfo } from '../types';
import { useCorePageState } from '../provider';
import { useMobile } from '../../context/mobile/mobile.provider';
import classNames from 'classnames';

type Props = {
  heading: PageHeading;
  pageParams: Params;
  updateSettings: (updates: Params) => void;
  page: PageInfo;
};
function Heading({ heading, updateSettings, pageParams }: Props) {
  const { dsl, trayType, setCustomizationTray, setIsFilterTray } = useCorePageState();
  const [usesFilters, setUsesFilters] = useState<boolean | undefined>();
  const { perform } = useUiAction();
  const { openDrawer, closeDrawer } = ClientDrawer.useClientDrawer();
  const { getAnalyticTrackingTags, trackPageEvent } = useAnalyticsContext();
  const context = ContextManager.getInstance().get();
  const disableCopyLinkButton = shouldDisableCopyLink(dsl);
  const disableFilterTray = shouldDisableFilterTray(dsl);
  const { isMobile } = useMobile();

  useEffect(() => {
    const subscriber = (context: Context) => {
      if (context) {
        setUsesFilters(context.filters?.sections.length > 0);
      }
    };
    ContextManager.getInstance().subscribeToSettingsChanges(subscriber);
    return () => ContextManager.getInstance().unsubscribeFromSettingsChanges(subscriber);
  }, []);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (usesFilters === false || disableFilterTray) {
      closeDrawer();
    }
  }, [usesFilters, disableFilterTray]);

  if (!heading) {
    return null;
  }

  const { title, backLink } = heading;

  const handleBack = () => {
    const uiAction: UiAction = {
      type: 'NAVIGATE_TO_PATH',
      payload: {
        path: backLink.path,
      },
    };
    perform(uiAction);
  };

  const handleCreateViewState = (): UseRestoreSavedViewV1 => {
    const context = ContextManager.getInstance().get();
    return {
      version: SavedViewVersion.v1,
      settingValues: context.settings.values,
      filterValues: context.filters?.values || {},
      pageSettings: PageSettingsManager.getInstance().get(),
    };
  };

  const getCopyLinkTrackingTags = (): SavedViewButtonsTrackingTags => {
    return getAnalyticTrackingTags(EventName.PageElementClicked, {
      elementType: 'Link',
      elementCategory: 'Copy Link',
      elementName: 'Copy Link',
    });
  };

  const filterToggleButtonOnClick = (expanded: boolean) => {
    // if the page is in custom tier view, and the custom price tray is open, 'close' it and open the filter tray over it
    if (trayType) {
      setCustomizationTray(null);
      setIsFilterTray(true);
      openDrawer();
    }
    if (!expanded) {
      setIsFilterTray(true);
      return;
    }

    const globalFilterApplied = getAppliedFilters(context.filters).globalFilterApplied;
    const globalFilterAppliedValue = Array.isArray(globalFilterApplied) ? globalFilterApplied[0] : globalFilterApplied;

    trackPageEvent(...getFilterEventDetails('Save filter', globalFilterAppliedValue));
  };

  return (
    <div
      className={classNames('core-page-header flex gap-4 bg-light', {
        'mobile-header': isMobile,
        'stick-to-app-header': !isMobile,
        'items-center': !isMobile,
      })}
    >
      <div className="flex-grow">
        {backLink && (
          <button onClick={handleBack} className="text-left text-sm text-brand hover:underline hover:text-brand-dark">
            {backLink.label || 'Back'}
          </button>
        )}
        <div className={`flex gap-4 items-center`}>
          <PageTitle title={title} pageParams={pageParams} updateSettings={updateSettings} />
        </div>
      </div>
      {/* added mobile-wrap class for < 758px screens */}
      <div className={`flex ${isMobile ? 'mobile-header-buttons gap-1' : ''}`}>
        {!disableCopyLinkButton && (
          <CopySavedViewLinkButton
            createViewStateFn={handleCreateViewState}
            getTrackingTags={getCopyLinkTrackingTags}
          />
        )}
        {usesFilters && !disableFilterTray && (
          <div className="flex-grow flex items-center justify-end pl-4">
            <FilterToggleButton onClick={filterToggleButtonOnClick} />
          </div>
        )}
      </div>
    </div>
  );
}

export { Heading };
